import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MessageThread {
  id: string;
  tenantId: string;
  clientId: string;
  clientName: string;
  clientEmail: string | null;
  subject: string | null;
  lastMessagePreview: string;
  lastMessageAt: string;
  lastMessageByStaff: boolean;
  unreadCount: number;
  isArchived: boolean;
  createdAt: string;
}

export interface Message {
  id: string;
  threadId: string;
  senderId: string;
  senderName: string;
  senderType: 'staff' | 'client';
  content: string;
  isInternalNote: boolean;
  attachments: MessageAttachment[];
  readAt: string | null;
  createdAt: string;
}

export interface MessageAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
}

// ─── Request Types ───────────────────────────────────────────────────────────

export interface ThreadsListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  isArchived?: boolean;
}

export interface SendMessageRequest {
  threadId: string;
  content: string;
  isInternalNote?: boolean;
  attachments?: File[];
}

export interface CreateThreadRequest {
  clientEmail: string;
  subject?: string;
  content: string;
}

/** Paginated response */
interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Query Keys ──────────────────────────────────────────────────────────────

const messageKeys = {
  all: ['messages'] as const,
  threads: () => [...messageKeys.all, 'threads'] as const,
  threadList: (params: ThreadsListParams) => [...messageKeys.threads(), params] as const,
  messages: () => [...messageKeys.all, 'thread-messages'] as const,
  threadMessages: (threadId: string) => [...messageKeys.messages(), threadId] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Hook to fetch paginated list of message threads (client conversations).
 * Validates: Requirements 19.1
 */
export function useMessageThreads(params: ThreadsListParams = {}) {
  return useQuery({
    queryKey: messageKeys.threadList(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.set('page', String(params.page));
      if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));
      if (params.search) queryParams.set('search', params.search);
      if (params.isArchived !== undefined) queryParams.set('isArchived', String(params.isArchived));

      const response = await apiClient.get<ApiResponse<PaginatedResponse<MessageThread>>>(
        `/messages/threads?${queryParams.toString()}`,
      );
      return response.data.data;
    },
    // Poll for new conversations, but only while the tab is focused, and don't
    // keep polling in the background — avoids piling up requests.
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
    staleTime: 10000,
  });
}

/**
 * Hook to fetch messages within a specific thread.
 */
export function useThreadMessages(threadId: string) {
  return useQuery({
    queryKey: messageKeys.threadMessages(threadId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Message[]>>(
        `/messages/threads/${threadId}/messages`,
      );
      return response.data.data;
    },
    enabled: !!threadId,
    // Poll the open thread for new replies, but pause when the tab is
    // backgrounded so requests don't accumulate.
    refetchInterval: 15000,
    refetchIntervalInBackground: false,
    staleTime: 5000,
  });
}

/**
 * Hook to send a message in a thread.
 * Supports text messages, internal notes, and file attachments.
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SendMessageRequest) => {
      let response;

      if (data.attachments && data.attachments.length > 0) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('content', data.content);
        if (data.isInternalNote) formData.append('isInternalNote', 'true');
        data.attachments.forEach((file) => formData.append('attachments', file));

        response = await apiClient.post<ApiResponse<Message>>(
          `/messages/threads/${data.threadId}/messages`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
      } else {
        // Use JSON for text-only messages
        response = await apiClient.post<ApiResponse<Message>>(
          `/messages/threads/${data.threadId}/messages`,
          {
            content: data.content,
            isInternalNote: data.isInternalNote || false,
          },
        );
      }

      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: messageKeys.threadMessages(variables.threadId) });
      queryClient.invalidateQueries({ queryKey: messageKeys.threads() });
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
    },
  });
}

/**
 * Hook to create a new message thread with a client.
 */
export function useCreateThread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateThreadRequest) => {
      const response = await apiClient.post<ApiResponse<MessageThread>>('/messages/threads', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.threads() });
    },
  });
}

/**
 * Threads with an in-flight (or completed) mark-read this session. A module-
 * level guard so no caller — even a mis-firing effect — can trigger repeated
 * mark-read requests for the same thread and cause a request storm.
 */
const markReadInFlight = new Set<string>();

/**
 * Hook to mark a thread as read.
 */
export function useMarkThreadRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (threadId: string) => {
      // Skip if a mark-read for this thread already went out this session.
      if (markReadInFlight.has(threadId)) return;
      markReadInFlight.add(threadId);
      await apiClient.post(`/messages/threads/${threadId}/read`);
    },
    onSuccess: (_, threadId) => {
      // Surgically zero the thread's unread count in every cached thread list
      // instead of a broad refetch. Avoids the refetch → new-objects →
      // re-render churn that previously fed a request loop. Also refresh the
      // sidebar unread badge.
      queryClient.setQueriesData<PaginatedResponse<MessageThread>>(
        { queryKey: messageKeys.threads() },
        (prev) => {
          if (!prev?.items) return prev;
          return {
            ...prev,
            items: prev.items.map((t) => (t.id === threadId ? { ...t, unreadCount: 0 } : t)),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
    },
  });
}
